import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Trophy, Calendar, Disc3, Upload, Star, Zap, Megaphone, Facebook, Globe } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1580397424400-2f12fdf0b3e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const STEPS = [
  { icon: Upload, title: "Drop your tracks", text: "Artists upload throughout the month. Every track in the catalog is in the running." },
  { icon: Star, title: "10 get chosen", text: "At the end of each month, 10 standout tracks are hand-picked by the FTO team." },
  { icon: Disc3, title: "Compilation drops", text: "The chosen 10 are pressed into an official monthly compilation album, released on FTO." },
];

export default function Compilation() {
  const { isAdmin } = useAuth();
  const nav = useNavigate();

  return (
    <div>
      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
        </div>
        <div className="relative px-6 md:px-12 py-16 md:py-24 max-w-3xl">
          <div className="overline text-xs text-accent mb-4 flex items-center gap-2"><Trophy size={14} /> Monthly Program</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight mb-4">
            10 TRACKS.<br />EVERY MONTH.<br /><span className="text-primary">ONE COMPILATION.</span>
          </h1>
          <p className="text-sm md:text-base text-muted-foreground max-w-xl">
            Every single month, <span className="text-white font-semibold">10 tracks are chosen</span> to feature on an official
            compilation album — released right here on FTO. Get your music heard. Make the cut.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-12 py-12">
        <div className="flex items-center gap-2 overline text-xs text-accent mb-8"><Calendar size={14} /> How it works</div>
        <div className="grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} data-testid={`comp-step-${i}`} className="bg-card border border-border p-6 hover:border-primary transition-colors">
              <div className="flex items-center justify-between mb-4">
                <s.icon className="text-primary" size={28} />
                <span className="font-display text-4xl font-bold text-white/10">0{i + 1}</span>
              </div>
              <div className="font-display text-xl font-bold mb-2">{s.title}</div>
              <p className="text-sm text-muted-foreground">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FREE PROMO CALLOUT */}
      <section className="px-6 md:px-12 pb-4">
        <div data-testid="comp-promo" className="bg-accent text-black p-8 md:p-10 max-w-3xl">
          <div className="flex items-center gap-2 overline text-xs font-bold mb-3"><Megaphone size={16} /> Make the cut = free promo</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4 leading-tight">
            WE PUSH YOUR MUSIC. FOR FREE.
          </h2>
          <p className="text-sm md:text-base font-medium mb-6 max-w-xl">
            If your track makes the cut, FTO promotes your music across our channels at <span className="font-bold underline">zero cost to you</span> — getting your sound in front of new ears.
          </p>
          <div className="flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 text-xs overline font-bold"><Facebook size={16} /> Facebook</span>
            <a href="https://popdatish.com" target="_blank" rel="noopener noreferrer" data-testid="comp-popdatish-link" className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 text-xs overline font-bold hover:bg-primary transition-colors"><Globe size={16} /> popdatish.com</a>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-16 pt-8">
        <div className="bg-card border-2 border-primary p-8 md:p-10 block-shadow max-w-3xl">
          <div className="overline text-xs text-accent mb-3 flex items-center gap-2"><Zap size={14} /> Get in the running</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-4">SUBMIT. STREAM. MAKE THE CUT.</h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-xl">
            Artists: send your tracks through the booking line to get uploaded to FTO. Listeners: go premium to stream every
            compilation full-length and back the artists who made it.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="comp-submit-btn" onClick={() => nav("/artist")} className="bg-primary hover:bg-white hover:text-primary rounded-none font-bold overline h-12 px-6">
              Submit a Track
            </Button>
            <Button data-testid="comp-browse-btn" onClick={() => nav("/browse")} variant="outline" className="rounded-none border-2 font-bold overline h-12 px-6">
              Browse Catalog
            </Button>
            {isAdmin && (
              <Button data-testid="comp-admin-btn" onClick={() => nav("/admin")} variant="outline" className="rounded-none border-2 border-accent text-accent hover:bg-accent hover:text-black font-bold overline h-12 px-6">
                Build Compilation (Admin)
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
